import React from "react";
import { useTranslation } from "react-i18next";
import type { InstallmentPurchase } from "../types";
import { formatInterestRate, formatShortDate } from "../utils/installmentsFormat";
import { formatCurrency } from "@/utils/formatters";
import { AppButton, StatTile } from "@/components/ui/primitives";
import { ModalShell } from "@/components/ui/ModalShell";

interface InstallmentsDetailModalProps {
  purchase: InstallmentPurchase | null;
  isOpen: boolean;
  onClose: () => void;
}

export const InstallmentsDetailModal: React.FC<InstallmentsDetailModalProps> = ({
  purchase,
  isOpen,
  onClose,
}) => {
  const { t } = useTranslation(["installments", "common"]);

  if (!isOpen || !purchase) return null;

  const interestRateValue = purchase.interestRate == null ? (
    <span className="inline-flex rounded-full bg-[var(--app-warning-chip-bg)] px-3 py-1 text-xs font-semibold text-[var(--app-warning-text)]">
      {t("installments.interestRateMissing")}
    </span>
  ) : (
    <span>{t("installments.interestRateKnown", { rate: formatInterestRate(purchase.interestRate) })}</span>
  );
  const formatInterestValue = (value: number | null) =>
    value == null ? t("installments.interestUnknown") : formatCurrency(value);

  return (
    <ModalShell
      isOpen={isOpen && Boolean(purchase)}
      onClose={onClose}
      title={purchase.name}
      subtitle={t("installments.paymentSchedule")}
      labelledBy="installment-detail-title"
      mobileSheet
      maxWidthClassName="max-w-lg"
      panelClassName="max-h-[min(calc(100dvh-1rem),760px)]"
      contentClassName="px-5 py-5 sm:px-6"
      footer={(
        <div className="flex justify-end">
          <AppButton type="button" tone="secondary" size="sm" onClick={onClose}>
            {t("common.actions.close")}
          </AppButton>
        </div>
      )}
    >
      <dl className="mb-6 grid grid-cols-1 gap-2 sm:grid-cols-3">
        <StatTile
          label={t("installments.annualInterestRate")}
          value={interestRateValue}
        />
        <StatTile
          label={t("installments.totalInterest")}
          value={formatInterestValue(purchase.totalInterest)}
        />
        <StatTile
          label={t("installments.remainingInterest")}
          value={formatInterestValue(purchase.remainingInterest)}
          tone={purchase.remainingInterest != null && purchase.remainingInterest > 0 ? "danger" : "default"}
        />
      </dl>
      <div className="space-y-3">
        {purchase.schedule.map((payment) => (
          <div
            key={payment.id}
            className="flex items-center justify-between gap-3 rounded-[var(--ds-radius-md)] border border-[var(--app-border)] px-4 py-3"
          >
            <div>
              <p className="text-sm font-medium text-[var(--app-text)]">
                {formatShortDate(payment.dueDate)}
              </p>
              <p className="text-xs text-[var(--app-muted-text)]">
                {payment.status === "paid" ? t("installments.paidLabel") : t("installments.pending")}
              </p>
            </div>
            <div className="text-right">
              <p className="text-sm font-semibold text-[var(--app-text)]">
                {formatCurrency(payment.amount)}
              </p>
              <span
                className={`inline-flex items-center rounded-full px-2 py-1 text-xs font-medium ${
                  payment.status === "paid"
                    ? "bg-[var(--app-primary-soft)] text-[var(--app-success)]"
                    : "bg-[var(--app-accent-soft)] text-[var(--app-warning)]"
                }`}
              >
                {payment.status === "paid" ? t("installments.paidLabel") : t("installments.pending")}
              </span>
            </div>
          </div>
        ))}
      </div>
    </ModalShell>
  );
};
